/**
 * Reports-Seite (Spec 429 Increment D) — QuickSight-Analytics-Embed.
 *
 * Zeigt das der Rolle zustehende Dashboard eingebettet: Plattform-Rollen die Plattform-Reports,
 * Merchant-Rollen den (group-RLS-gefilterten) Merchant-Report. Der eigentliche Zeilenfilter +
 * die reportKey-Entitlement sitzen serverseitig (Mint-Handler isEntitled + QuickSight-RLS);
 * diese Seite waehlt nur den passenden reportKey und rendert die gemintete Embed-URL.
 *
 * Solange die Dashboards noch nicht existieren (dashboardId nicht im SSM-Baum) oder die Stage
 * nicht im Pilot ist, antwortet der Mint-Endpunkt 503 → hier der „wird vorbereitet"-Zustand.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, Button } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useReportEmbed } from '@/hooks/use-report-embed';
import { REPORT_AUDIENCE, type ReportKey } from '@/lib/api/reports';
import { isPlatformRole, type UserRole } from '@/types/auth';
import { ApiError } from '@/lib/api/client';
import { getIntlLocale } from '@/lib/utils/format';

/**
 * Panel-seitig ausgeblendete reportKeys (Owner 2026-07-07): der KPI-Streifen entfaellt als
 * eigener Report — Plattform-Rollen landen direkt im Plattform-Überblick, die Report-
 * Umschalt-Leiste verschwindet (1 Key je Audience) und der Inhalt rutscht nach oben.
 * Server-Entitlement + QuickSight-Asset bleiben unangetastet (reversibel).
 */
const HIDDEN_REPORT_KEYS: readonly ReportKey[] = ['kpi-overview-platform'];

/** reportKeys, die eine Rolle sehen darf (grob; die feine Entitlement erzwingt der Server). */
function reportKeysForRole(role: UserRole): ReportKey[] {
  const audience = isPlatformRole(role) ? 'platform' : 'merchant';
  return (Object.keys(REPORT_AUDIENCE) as ReportKey[]).filter(
    (k) => REPORT_AUDIENCE[k] === audience && !HIDDEN_REPORT_KEYS.includes(k),
  );
}

export function ReportsPage() {
  const { t, i18n } = useTranslation('reports');
  const { user } = useAuth();
  const availableKeys = user ? reportKeysForRole(user.role) : [];
  const [selected, setSelected] = useState<ReportKey | undefined>(availableKeys[0]);

  const activeKey = selected ?? availableKeys[0];
  const { data, isLoading, isError, error } = useReportEmbed(
    activeKey as ReportKey,
    Boolean(activeKey),
  );

  // 503 = Pilot-dunkel / dashboardId noch nicht publiziert → freundlicher „wird vorbereitet".
  const isNotReady = isError && error instanceof ApiError && [404, 503].includes(error.status);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('title')} description={t('description')} />

      {availableKeys.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {availableKeys.map((key) => (
            <Button
              key={key}
              variant={key === activeKey ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelected(key)}
            >
              {t(`reportKey.${key}`)}
            </Button>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading && <p className="text-sm text-muted-foreground">{t('state.loading')}</p>}

          {isNotReady && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <p className="text-base font-medium">{t('state.notReady.title')}</p>
              <p className="max-w-md text-sm text-muted-foreground">{t('state.notReady.body')}</p>
            </div>
          )}

          {isError && !isNotReady && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <p className="text-base font-medium">{t('state.error.title')}</p>
              <p className="max-w-md text-sm text-muted-foreground">
                {error instanceof Error ? error.message : t('state.error.body')}
              </p>
            </div>
          )}

          {data?.embedUrl && (
            <div className="flex flex-col gap-2">
              <iframe
                title={t(`reportKey.${activeKey}`)}
                // QS-Chrome-Sprache folgt der Panel-Sprache: `locale` ist ein normaler
                // Query-Param der Embed-URL (dasselbe tut das offizielle Embedding-SDK via
                // contentOptions.locale). Unsere Dashboard-INHALTE sind einsprachig Englisch.
                src={`${data.embedUrl}&locale=${i18n.language?.startsWith('de') ? 'de-DE' : 'en-US'}`}
                className="h-[70vh] w-full rounded-md border-0"
                // QuickSight-Embed benoetigt diese Freigaben (Fullscreen-Widget-Aktionen).
                allow="fullscreen"
              />
              {(() => {
                // i18n-korrekte Datumsformatierung (aktive Sprache statt Browser-Default) +
                // Guard gegen Invalid Date, falls das Backend ein leeres/kaputtes dataAsOf liefert.
                const asOf = new Date(data.dataAsOf);
                const asOfLabel = Number.isNaN(asOf.getTime())
                  ? '—'
                  : asOf.toLocaleString(getIntlLocale());
                return (
                  <p className="text-xs text-muted-foreground">
                    {t('dataAsOf', { value: asOfLabel })}
                    {data.completeness === 'provisional' && ` · ${t('provisional')}`}
                  </p>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
